import "./LyneBoardPicker.scss";

import { SbbAutocomplete } from "@sbb-esta/lyne-react/autocomplete";
import { SbbLoadingIndicatorCircle } from "@sbb-esta/lyne-react/loading-indicator-circle";
import { SbbOption } from "@sbb-esta/lyne-react/option";
import { useQuery } from "@tanstack/react-query";
import { FormField } from "components/Forms/FormField";
import { createLogger } from "logging";
import { BoardLookup } from "models/BoardLookup";
import { useApi, useTranslations } from "providers";
import { useRef, useState } from "react";

import { LyneFormField } from "./LyneFormField";
import { LyneInputReset } from "./LyneInputReset";
import { LynePickerInput, LynePickerInputHandle } from "./LynePickerInput";

const logger = createLogger("LyneBoardPicker");

interface LyneBoardPickerProps extends FormField {
  boardId: string;
  permission: "CanView" | "CanEdit";
  onSelect: (board: BoardLookup) => void;
}

export const LyneBoardPicker: React.FC<LyneBoardPickerProps> = ({
  name,
  boardId,
  permission,
  disabled = false,
  onSelect,
  label,
  contextHelp,
}) => {
  const i18n = useTranslations();
  const pickerResetRef = useRef<LynePickerInputHandle>(null);
  const { boardSendToApi } = useApi();

  const [searchValue, setSearchValue] = useState("");

  const queryResult = useQuery({
    queryKey: [`BoardSendToSearch|${boardId}|${permission}|${searchValue}`],
    queryFn: async () => {
      const data = await boardSendToApi.search(boardId, searchValue, permission);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: !!searchValue && !!boardId,
  });
  const isLoading = queryResult.isPending;
  const boards = queryResult.data || [];

  const handleReset = () => {
    logger.debug(`handleReset(): `);
    setSearchValue("");
    pickerResetRef.current?.reset();
  };

  return (
    <LyneFormField
      name={name}
      className="lyne-picker board-picker"
      required={permission === "CanEdit"}
      label={label}
      contextHelp={contextHelp}
    >
      <LynePickerInput
        logger={logger}
        resetRef={pickerResetRef}
        disabled={disabled}
        placeholder={i18n.BoardPicker_Placeholder}
        onDebouncedChange={setSearchValue}
      />

      {isLoading && <SbbLoadingIndicatorCircle color="smoke" />}

      <LyneInputReset onClick={handleReset} disabled={!searchValue || disabled} />

      {boards.length > 0 && (
        <SbbAutocomplete>
          {boards.map((board) => (
            <SbbOption
              key={board.id}
              value={board.name}
              onOptionselected={(e) => {
                e.preventDefault();
                handleReset();
                onSelect(board);
              }}
            >
              {board.longName || board.name}
            </SbbOption>
          ))}
        </SbbAutocomplete>
      )}
    </LyneFormField>
  );
};
